import { useMemo } from 'react';

import { useReportingMonetizationPricesQuery } from '@cast/shared-trial-data-access';

import {
  applyReportingPricesToPlans,
  ENTERPRISE_PLAN,
  REPORTING_PLANS,
  recommendReportingPlan,
  resolveReportingPaidPlan,
  type ReportingPlan,
  type ReportingPlanId,
} from './reportingPlans';

/**
 * Plan catalog with Stripe `/prices` overlaid when available; falls back to
 * hardcoded labels while loading or on error.
 */
export const useReportingPlansWithPrices = () => {
  const { data, isLoading, isError } = useReportingMonetizationPricesQuery();

  const plans = useMemo(
    () => applyReportingPricesToPlans(data?.prices),
    [data?.prices]
  );

  const resolvePaidPlan = (
    planId: string | undefined
  ): ReportingPlan | undefined => {
    if (!planId) {
      return undefined;
    }
    const plan = plans.find((p) => p.id === planId);
    if (!(plan?.cta === 'checkout' && plan.vcpuCap != null)) {
      return undefined;
    }
    return plan;
  };

  const resolvePlan = (planId: ReportingPlanId | undefined): ReportingPlan => {
    if (planId === 'enterprise') {
      return ENTERPRISE_PLAN;
    }
    return (
      plans.find((p) => p.id === planId) ??
      resolveReportingPaidPlan(planId) ??
      REPORTING_PLANS[0]
    );
  };

  return {
    plans,
    enterprisePlan: ENTERPRISE_PLAN,
    recommendPlan: (avgProvisionedVcpu: number) =>
      recommendReportingPlan(avgProvisionedVcpu, plans),
    resolvePaidPlan,
    resolvePlan,
    isLoadingPrices: isLoading,
    isPricesError: isError,
  };
};
